import React from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';

const StyleCard = ({ 
  style, 
  selected, 
  onClick, 
  disabled = false,
  loading = false 
}) => {
  const handleClick = () => {
    if (!disabled && !loading && onClick) {
      onClick(style);
    }
  };

  return (
    <Card
      onClick={handleClick}
      sx={{
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        border: selected ? 2 : 1,
        borderColor: selected 
          ? 'primary.main' 
          : disabled 
            ? 'grey.300' 
            : 'grey.300',
        position: 'relative',
        transition: 'all 0.3s ease',
        opacity: disabled ? 0.6 : 1,
        '&:hover': {
          transform: disabled || loading ? 'none' : 'scale(1.05)',
          boxShadow: disabled || loading ? 1 : 3,
          borderColor: disabled ? 'grey.300' : 'primary.main'
        }
      }}
    >
      {/* Selected indicator */}
      {selected && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            bgcolor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CheckCircle sx={{ color: 'white', fontSize: 20 }} />
        </Box>
      )}

      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2
          }}
        >
          <CircularProgress size={30} />
        </Box>
      )}

      {/* Thumbnail image */}
      <CardMedia
        component="img"
        height="140"
        image={style.thumbnailUrl}
        alt={style.name}
        sx={{
          objectFit: 'cover',
          opacity: selected ? 1 : disabled ? 0.5 : 0.9,
          transition: 'opacity 0.3s',
          bgcolor: style.category === 'haircut' ? '#667eea' : '#f5576c',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: '0.875rem',
          fontWeight: 'bold'
        }}
        onError={(e) => {
          // Fallback: show colored placeholder with text
          e.target.style.display = 'none';
          const parent = e.target.parentElement;
          if (parent && !parent.querySelector('.placeholder-text')) {
            const placeholder = document.createElement('div');
            placeholder.className = 'placeholder-text';
            placeholder.textContent = style.name;
            placeholder.style.cssText = `
              width: 100%;
              height: 140px;
              display: flex;
              align-items: center;
              justify-content: center;
              background: ${style.category === 'haircut' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'};
              color: white;
              font-weight: bold;
              font-size: 0.875rem;
            `;
            parent.appendChild(placeholder);
          }
        }}
      />

      <CardContent sx={{ p: 1.5 }}>
        <Typography 
          variant="subtitle1" 
          fontWeight="bold"
          sx={{ 
            mb: 0.5,
            fontSize: '0.9rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {style.name}
        </Typography>
        
        {style.description && (
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: '0.75rem',
              lineHeight: 1.2
            }}
          >
            {style.description}
          </Typography>
        )}

        {style.price && (
          <Chip
            label={`$${style.price}`}
            size="small"
            color="primary"
            sx={{ mt: 0.5, fontSize: '0.7rem', height: '20px' }}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default StyleCard;
